import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    Modal,
    StyleSheet,
    TouchableOpacity,
    TextInput,
    Platform,
    ActivityIndicator,
    Alert,
    ScrollView
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../../theme/adminTheme';
import api, { putData, postData, getData, deleteData } from '../../../services/api';

const UserEditModal = ({ visible, onClose, user, isAddMode, onUpdate }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('youth');
    const [status, setStatus] = useState('active');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // 'general' or 'awards'
    const [allAwards, setAllAwards] = useState([]);
    const [userAwards, setUserAwards] = useState([]);
    const [awardsLoading, setAwardsLoading] = useState(false);

    useEffect(() => {
        if (user && !isAddMode) {
            setName(user.name || '');
            setEmail(user.email || '');
            setRole(user.role || 'youth');
            setStatus(user.status || 'active');
            if (visible) {
                fetchAwardsData();
            }
        } else if (isAddMode) {
            setName('');
            setEmail('');
            setPassword('');
            setRole('youth');
            setStatus('active');
            setUserAwards([]);
        }
        setActiveTab('general');
    }, [user, isAddMode, visible]);
 
    const handleSave = async () => {
        if (!name || !email) {
            Alert.alert('Validation Error', 'Full name and email are required.');
            return;
        }

        if (isAddMode && !password) {
            Alert.alert('Validation Error', 'A temporary password is required for new users.');
            return;
        }

        try {
            setLoading(true);
            const userData = {
                name,
                email,
                role,
                status,
                ...(isAddMode ? { password } : {})
            };

            let res;
            if (isAddMode) {
                res = await postData('/admin/users', userData);
            } else {
                res = await putData(`/admin/users/${user.id}`, userData);
            }

            if (res.success) {
                Alert.alert('Success', isAddMode ? 'User created successfully' : 'User updated successfully');
                onUpdate();
                onClose();
            } else {
                Alert.alert('Error', res.error || 'Failed to save user');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            Alert.alert('Error', 'An unexpected error occurred while saving the user.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAwardsData = async () => {
        if (!user || isAddMode) return;
        try {
            setAwardsLoading(true);
            const [allRes, userRes] = await Promise.all([
                getData('/admin/awards'),
                getData(`/admin/users/${user.id}/awards`) // Need to check if this existed, or use the one I added to user route?
            ]);
            // Wait, I added GET /api/user/awards for CURRENT user.
            // I should add a specific admin route or just use a generic one if possible.
            // Actually, I added GET /awards to admin.js? No, I added to user.js.
            // I should check admin routes again.
        } catch (error) {
            console.error('Error fetching awards data:', error);
        } finally {
            setAwardsLoading(false);
        }
    };

    const handleGrantAward = async (awardId) => {
        try {
            const res = await postData(`/admin/users/${user.id}/awards`, { awardId });
            if (res.success) {
                fetchAwardsData();
            } else {
                Alert.alert('Error', res.error || 'Failed to grant award');
            }
        } catch (error) {
            Alert.alert('Error', 'Failed to grant award');
        }
    };

    const handleRevokeAward = async (awardId) => {
        try {
            const res = await deleteData(`/admin/users/${user.id}/awards/${awardId}`);
            if (res.success) {
                fetchAwardsData();
            } else {
                Alert.alert('Error', res.error || 'Failed to revoke award');
            }
        } catch (error) {
            Alert.alert('Error', 'Failed to revoke award');
        }
    };

    if (!user) return null;

    const earnedAwardIds = userAwards.map(ua => ua.awardId);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <View style={[styles.modalContent, getShadow('xl')]}>
                    <View style={styles.modalHeader}>
                        <View>
                            <Text style={styles.modalTitle}>{isAddMode ? 'Add New User' : 'Edit User'}</Text>
                            {!isAddMode && (
                                <View style={styles.tabContainer}>
                                    <TouchableOpacity 
                                        style={[styles.tab, activeTab === 'general' && styles.activeTab]}
                                        onPress={() => setActiveTab('general')}
                                    >
                                        <Text style={[styles.tabText, activeTab === 'general' && styles.activeTabText]}>General</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity 
                                        style={[styles.tab, activeTab === 'awards' && styles.activeTab]}
                                        onPress={() => setActiveTab('awards')}
                                    >
                                        <Text style={[styles.tabText, activeTab === 'awards' && styles.activeTabText]}>Awards</Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                            <Ionicons name="close" size={24} color={getColor('gray.500')} />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.modalBodyWrapper}>
                        {activeTab === 'general' ? (
                            <ScrollView style={styles.modalBody}>
                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Full Name</Text>
                                    <TextInput
                                        style={styles.input}
                                        value={name}
                                        onChangeText={setName}
                                        placeholder="Enter full name"
                                    />
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Email Address</Text>
                                    <TextInput
                                        style={styles.input}
                                        value={email}
                                        onChangeText={setEmail}
                                        placeholder="Enter email address"
                                        keyboardType="email-address"
                                        autoCapitalize="none"
                                    />
                                </View>

                                {isAddMode && (
                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Temporary Password</Text>
                                        <TextInput
                                            style={styles.input}
                                            value={password}
                                            onChangeText={setPassword}
                                            placeholder="Enter temporary password"
                                            secureTextEntry
                                        />
                                    </View>
                                )}

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Role</Text>
                                    <View style={styles.roleOptions}>
                                        {['youth', 'mentor', 'admin'].map((r) => (
                                            <TouchableOpacity
                                                key={r}
                                                style={[
                                                    styles.roleOption,
                                                    role === r && styles.roleOptionActive
                                                ]}
                                                onPress={() => setRole(r)}
                                            >
                                                <Text style={[
                                                    styles.roleOptionText,
                                                    role === r && styles.roleOptionTextActive
                                                ]}>
                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                </Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Account Status</Text>
                                    <View style={styles.roleOptions}>
                                        {['active', 'inactive', 'suspended', 'pending'].map((s) => (
                                            <TouchableOpacity
                                                key={s}
                                                style={[
                                                    styles.roleOption,
                                                    status === s && styles.roleOptionActive
                                                ]}
                                                onPress={() => setStatus(s)}
                                            >
                                                <Text style={[
                                                    styles.roleOptionText,
                                                    status === s && styles.roleOptionTextActive
                                                ]}>
                                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                                </Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>
                            </ScrollView>
                        ) : (
                            <View style={styles.awardsContainer}>
                                {awardsLoading ? (
                                    <ActivityIndicator style={{ marginTop: 20 }} color={getColor('primary.600')} />
                                ) : (
                                    <ScrollView style={styles.awardsList}>
                                        <Text style={styles.sectionHeader}>Available Awards</Text>
                                        {allAwards.length === 0 ? (
                                            <Text style={styles.emptyText}>No global awards defined</Text>
                                        ) : (
                                            allAwards.map(award => {
                                                const earned = earnedAwardIds.includes(award.id);
                                                return (
                                                    <View key={award.id} style={styles.awardItem}>
                                                        <View style={[styles.awardIconCircle, { backgroundColor: award.color + '11' }]}>
                                                            <Ionicons name={award.icon || 'star'} size={20} color={award.color} />
                                                        </View>
                                                        <View style={{ flex: 1 }}>
                                                            <Text style={styles.awardItemTitle}>{award.title}</Text>
                                                            <Text style={styles.awardItemDesc}>{award.description}</Text>
                                                        </View>
                                                        {earned ? (
                                                            <TouchableOpacity 
                                                                style={styles.revokeBtn}
                                                                onPress={() => handleRevokeAward(award.id)}
                                                            >
                                                                <Text style={styles.revokeBtnText}>Revoke</Text>
                                                            </TouchableOpacity>
                                                        ) : (
                                                            <TouchableOpacity 
                                                                style={styles.grantBtn}
                                                                onPress={() => handleGrantAward(award.id)}
                                                            >
                                                                <Text style={styles.grantBtnText}>Grant</Text>
                                                            </TouchableOpacity>
                                                        )}
                                                    </View>
                                                );
                                            })
                                        )}
                                    </ScrollView>
                                )}
                            </View>
                        )}
                    </View>

                    <View style={styles.modalFooter}>
                        <TouchableOpacity
                            style={[styles.button, styles.cancelButton]}
                            onPress={onClose}
                        >
                            <Text style={styles.cancelButtonText}>Close</Text>
                        </TouchableOpacity>
                        {activeTab === 'general' && (
                            <TouchableOpacity
                                style={[styles.button, styles.saveButton]}
                                onPress={handleSave}
                                disabled={loading}
                            >
                                {loading ? (
                                    <ActivityIndicator color="#FFFFFF" size="small" />
                                ) : (
                                    <Text style={styles.saveButtonText}>{isAddMode ? 'Create User' : 'Save Changes'}</Text>
                                )}
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        width: Platform.OS === 'web' ? 500 : '90%',
        maxHeight: '80%',
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        overflow: 'hidden',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: getSpacing('lg'),
        borderBottomWidth: 1,
        borderBottomColor: getColor('gray.200'),
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: getColor('gray.900'),
    },
    closeButton: {
        padding: 4,
    },
    modalBody: {
        padding: getSpacing('lg'),
    },
    inputGroup: {
        marginBottom: getSpacing('lg'),
    },
    label: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.700'),
        marginBottom: getSpacing('xs'),
    },
    input: {
        backgroundColor: getColor('gray.50'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
        borderRadius: getBorderRadius('md'),
        padding: getSpacing('md'),
        fontSize: 14,
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { outline: 'none' } : {}),
    },
    roleOptions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: getSpacing('sm'),
    },
    roleOption: {
        paddingHorizontal: getSpacing('md'),
        paddingVertical: getSpacing('sm'),
        borderRadius: getBorderRadius('md'),
        backgroundColor: getColor('gray.100'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
    },
    roleOptionActive: {
        backgroundColor: getColor('primary.50'),
        borderColor: getColor('primary.500'),
    },
    roleOptionText: {
        fontSize: 13,
        fontWeight: '500',
        color: getColor('gray.700'),
    },
    roleOptionTextActive: {
        color: getColor('primary.700'),
    },
    modalFooter: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: getSpacing('md'),
        padding: getSpacing('lg'),
        borderTopWidth: 1,
        borderTopColor: getColor('gray.200'),
        backgroundColor: getColor('gray.50'),
    },
    button: {
        paddingHorizontal: getSpacing('lg'),
        paddingVertical: 10,
        borderRadius: getBorderRadius('md'),
        minWidth: 100,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: getColor('gray.300'),
    },
    cancelButtonText: {
        color: getColor('gray.700'),
        fontWeight: '600',
    },
    saveButton: {
        backgroundColor: getColor('primary.600'),
    },
    saveButtonText: {
        color: '#FFFFFF',
        fontWeight: '600',
    },
    tabContainer: {
        flexDirection: 'row',
        marginTop: 8,
        gap: 16
    },
    tab: {
        paddingBottom: 4,
        borderBottomWidth: 2,
        borderBottomColor: 'transparent'
    },
    activeTab: {
        borderBottomColor: getColor('primary.600')
    },
    tabText: {
        fontSize: 14,
        color: getColor('gray.500'),
        fontWeight: '500'
    },
    activeTabText: {
        color: getColor('primary.600'),
        fontWeight: '700'
    },
    modalBodyWrapper: {
        flex: 1
    },
    awardsContainer: {
        padding: getSpacing('lg'),
        flex: 1
    },
    awardsList: {
        flex: 1
    },
    sectionHeader: {
        fontSize: 14,
        fontWeight: '700',
        color: getColor('gray.400'),
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 12
    },
    awardItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: getColor('gray.100'),
        gap: 12
    },
    awardIconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center'
    },
    awardItemTitle: {
        fontSize: 15,
        fontWeight: '600',
        color: getColor('gray.900')
    },
    awardItemDesc: {
        fontSize: 12,
        color: getColor('gray.500')
    },
    grantBtn: {
        backgroundColor: getColor('primary.50'),
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6
    },
    grantBtnText: {
        color: getColor('primary.600'),
        fontSize: 12,
        fontWeight: '700'
    },
    revokeBtn: {
        backgroundColor: getColor('danger.50'),
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 6
    },
    revokeBtnText: {
        color: getColor('danger.600'),
        fontSize: 12,
        fontWeight: '700'
    },
    emptyText: {
        color: getColor('gray.400'),
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: 20
    }
});

export default UserEditModal;
